/** @format */
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { StateService } from '@uirouter/core';
import { CourseUnit, OpenUniversityCart, OpenUniversityProduct, OtmId } from 'common-typescript/types';
import { map, Observable, switchMap } from 'rxjs';
import { EnrichedAssessmentItemsForOpenUniProductQueryService, graphqlUtils } from 'sis-common';
import { AppErrorHandler, CourseUnitEntityService, OpenUniversityProductEntityService } from 'sis-components';
import { EnrichedAssItem } from '../../open-university-enrolment-wizard/open-university-enrolment-wizard.component';

interface EnrolledItemsData {
    openUniversityProducts: OpenUniversityProduct[];
    courseUnits: CourseUnit[];
    assessmentItemsData: EnrichedAssItem[];
}

/**
 * The "enrolment confirmed" view (Finnish: "Vahvistus valmis") reached after the studies have been activated in the study voucher
 * purchase flow. It is an adaptation of the normal open university path's confirmation completed view, showing the purchased product
 * packages with their study voucher price. The activated cart is received from the activation response via the route's cartParam.
 */
@Component({
    selector: 'app-study-voucher-confirmation-completed',
    standalone: false,
    templateUrl: './study-voucher-confirmation-completed.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyVoucherConfirmationCompletedComponent implements OnInit {
    @Input() openUniversityCart: OpenUniversityCart;

    enrolledItemsData$: Observable<EnrolledItemsData>;

    constructor(
        private readonly openUniversityProductEntityService: OpenUniversityProductEntityService,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly assessmentItemsQueryService: EnrichedAssessmentItemsForOpenUniProductQueryService,
        private readonly stateService: StateService,
        private readonly appErrorHandler: AppErrorHandler,
    ) {}

    ngOnInit(): void {
        // The cart is only available via the route's cartParam, which is lost when the user navigates away and returns
        // (e.g. via the browser's back button). Redirect to the frontpage instead of rendering a view with a null cart.
        if (!this.openUniversityCart) {
            this.goToFrontPage();
            return;
        }
        this.enrolledItemsData$ = this.getEnrolledItemsData();
    }

    goToSearch(): void {
        this.stateService.go('student.search.open-university', {}, { custom: { skipConfirmationDialog: true } });
    }

    goToFrontPage(): void {
        this.stateService.go('student.logged-in.frontpage', {}, { custom: { skipConfirmationDialog: true } });
    }

    private getEnrolledItemsData(): Observable<EnrolledItemsData> {
        const openUniversityProductIds: OtmId[] = this.openUniversityCart.items.map(item => item.openUniversityProductId);
        return this.openUniversityProductEntityService.getByIds(openUniversityProductIds).pipe(
            switchMap(openUniversityProducts =>
                this.courseUnitEntityService
                    .getByIds(openUniversityProducts.map(product => product.courseUnitId))
                    .pipe(
                        switchMap(courseUnits =>
                            this.getAssessmentItemsData(openUniversityProducts, courseUnits).pipe(
                                map(assessmentItemsData => ({ openUniversityProducts, courseUnits, assessmentItemsData })),
                            ),
                        ),
                    ),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private getAssessmentItemsData(
        openUniversityProducts: OpenUniversityProduct[],
        courseUnits: CourseUnit[],
    ): Observable<EnrichedAssItem[]> {
        const cuIdsAndCmIdsOfProducts = openUniversityProducts.map(product => ({
            courseUnitId: product.courseUnitId,
            completionMethodId: product.completionMethodId,
        }));
        const assessmentItemIds: OtmId[] = [
            ...new Set(
                courseUnits.flatMap(courseUnit =>
                    courseUnit.completionMethods
                        .filter(completionMethod =>
                            cuIdsAndCmIdsOfProducts.some(
                                o => o.courseUnitId === courseUnit.id && o.completionMethodId === completionMethod.localId,
                            ),
                        )
                        .flatMap(completionMethod => completionMethod.assessmentItemIds),
                ),
            ),
        ];
        return this.assessmentItemsQueryService
            .fetch({ variables: { assessmentItemIds } })
            .pipe(map(assessmentItemsData => graphqlUtils.removeTypeNames(assessmentItemsData.data.searchResult)));
    }
}
